import {
  EnumFormGenComponentType,
  type IFormGenSchema,
  type TFormGenUiSchema,
  type TJSONSchema,
} from "@artifact-management/json-dynamic-form";

const ADDRESS_REQUIRED_MESSAGE = "L'adresse est obligatoire si vous n'avez pas précisé que l'offre est à distance.";

export const socialOfferJsonSchema = {
  type: "object",
  $defs: {
    isTemporary: { properties: { permanent: { const: "temporary" } } },
    isNotRemote: { properties: { isRemote: { const: false } } },
    // Provider typed by hand (no _id): not yet a structure partner
    isNewProvider: {
      properties: { provider: { type: "object", required: ["name"], not: { required: ["_id"] } } },
      required: ["provider"],
    },
    hasProvider: { properties: { provider: { type: "object", required: ["name"] } }, required: ["provider"] },
  },
  properties: {
    name: { type: "string", minLength: 1, errorMessage: { minLength: "Ce champ est obligatoire" } },
    permanent: {
      type: "string",
      enum: ["permanent", "temporary"],
      oneOf: [
        { const: "permanent", title: "Permanente" },
        { const: "temporary", title: "Temporaire" },
      ],
      errorMessage: "Ce champ est obligatoire",
    },
    serviceDates: { type: "object", dateRange: {}, errorMessage: "Ce champ est obligatoire" },
    tags: {
      type: "array",
      items: { type: "object", setting: { domain: "status", settingType: "social_tag" } },
    },
    financingModes: {
      type: "array",
      items: { type: "object", setting: { domain: "settings", settingType: "financingModes" } },
    },
    description: { type: "string", format: "richtext" },
    beneficiaries: { type: "string", format: "richtext" },
    registrationModalities: { type: "string", format: "richtext" },
    url: { type: "string", format: "url" },
    isRemote: { type: "boolean" },
    address: { type: "object", nullable: true, address: {}, errorMessage: ADDRESS_REQUIRED_MESSAGE },
    provider: { type: "object", provider: {}, errorMessage: "Ce champ est obligatoire" },
    createStructurePartner: { type: "boolean" },
    contact: { type: "object", contact: {} },
    perimeter: {
      type: "array",
      items: { type: "object", perimeter: {} },
    },
  },
  additionalProperties: false,
  required: ["name", "permanent", "isRemote", "provider"],
  allOf: [
    {
      if: { $ref: "#/$defs/isTemporary" },
      then: { required: ["serviceDates"] },
    },
    {
      if: { $ref: "#/$defs/isNotRemote" },
      then: {
        required: ["address"],
        properties: { address: { type: "object", errorMessage: ADDRESS_REQUIRED_MESSAGE } },
      },
    },
  ],
} as const satisfies TJSONSchema;

export const socialOfferUiSchema: TFormGenUiSchema<typeof socialOfferJsonSchema>[] = [
  { type: EnumFormGenComponentType.INPUT, label: "Titre de l'offre *", scope: "name", columns: { xs: 12 } },
  {
    type: EnumFormGenComponentType.INPUT,
    label: "Type d'offre *",
    scope: "permanent",
    renderAs: "select",
    columns: { xs: 12, sm: 6 },
  },
  {
    type: EnumFormGenComponentType.INPUT,
    label: "Période *",
    scope: "serviceDates",
    columns: { xs: 12, sm: 6 },
    hidden: true,
    rules: [{ rule: "isTemporary", override: { hidden: false } }],
  },
  { type: EnumFormGenComponentType.INPUT, label: "Besoins", scope: "tags", columns: { xs: 12, sm: 6 }, newLine: true },
  {
    type: EnumFormGenComponentType.INPUT,
    label: "Type de financement",
    scope: "financingModes",
    columns: { xs: 12, sm: 6 },
  },
  { type: EnumFormGenComponentType.DIVIDER, columns: { xs: 12 } },
  {
    type: EnumFormGenComponentType.TITLE,
    title: "Présentation du service",
    columns: { xs: 12 },
  },
  { type: EnumFormGenComponentType.INPUT, label: "", scope: "description", columns: { xs: 12 }, height: 250 },
  {
    type: EnumFormGenComponentType.TITLE,
    title: "Qui peut bénéficier de ce service ?",
    columns: { xs: 12 },
  },
  { type: EnumFormGenComponentType.INPUT, label: "", scope: "beneficiaries", columns: { xs: 12 }, height: 250 },
  {
    type: EnumFormGenComponentType.INPUT,
    label: "Offre ouverte aux habitants de",
    scope: "perimeter",
    columns: { xs: 12 },
  },
  {
    type: EnumFormGenComponentType.TITLE,
    title: "Comment s'inscrire ?",
    columns: { xs: 12 },
  },
  {
    type: EnumFormGenComponentType.INPUT,
    label: "",
    scope: "registrationModalities",
    columns: { xs: 12 },
    height: 250,
  },
  { type: EnumFormGenComponentType.INPUT, label: "URL de l'offre", scope: "url", columns: { xs: 12 } },
  { type: EnumFormGenComponentType.DIVIDER, columns: { xs: 12 } },
  {
    type: EnumFormGenComponentType.TITLE,
    title: "Informations utiles",
    columns: { xs: 12 },
  },
  { type: EnumFormGenComponentType.INPUT, label: "Structure partenaire *", scope: "provider", columns: { xs: 12 } },
  {
    type: EnumFormGenComponentType.INPUT,
    label:
      "Voulez-vous ajouter ce partenaire à la base de donnée Horizon ? Une nouvelle page partenaire sera créée après l’ajout de cette offre.",
    scope: "createStructurePartner",
    columns: { xs: 12 },
    hidden: true,
    rules: [{ rule: "isNewProvider", override: { hidden: false } }],
  },
  {
    type: EnumFormGenComponentType.INPUT,
    label: "L'offre est disponible à distance",
    scope: "isRemote",
    columns: { xs: 12 },
  },
  {
    type: EnumFormGenComponentType.INPUT,
    label: "Lieu d'accueil",
    scope: "address",
    columns: { xs: 12 },
    rules: [{ rule: "isNotRemote", override: { label: "Lieu d'accueil *" } }],
  },
  {
    type: EnumFormGenComponentType.INPUT,
    label: "Contact",
    scope: "contact",
    columns: { xs: 12 },
    disabled: true,
    rules: [{ rule: "hasProvider", override: { disabled: false } }],
  },
];

export const socialOfferFormSchema: IFormGenSchema<typeof socialOfferJsonSchema> = {
  version: "1.0",
  jsonSchema: socialOfferJsonSchema,
  uiSchema: socialOfferUiSchema,
};
